import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Position {
  q: number
  r: number
}

export interface Move {
  start: Position
  end: Position
  path: Position[]
}

export interface Piece {
  position: Position
  player: number
}

export interface BotBattleConfig {
  enabled: boolean
  bot1Difficulty: 'easy' | 'medium' | 'hard' | 'advanced'
  bot2Difficulty: 'easy' | 'medium' | 'hard' | 'advanced'
  bot1First: boolean
}

export interface GameState {
  gameId: string | null
  pieces: Piece[]
  currentPlayer: number
  winner: number | null
  legalMoves: Move[]
  moveHistory: Move[]
  selectedPiece: Position | null
  validMoves: Move[]
  aiDifficulty: 'easy' | 'medium' | 'hard' | 'advanced'
  playerColor: number
  marbleStyle: 'alley-glass'
  isAIThinking: boolean
  animatingPiece: { from: Position; to: Position; path?: Position[] } | null
  botBattle: BotBattleConfig
}

export interface SavedGame {
  id: string
  date: string
  result: 'win' | 'loss' | 'draw'
  moves: Move[]
  opponent: string
  moveCount: number
}

export interface GameStats {
  gamesPlayed: number
  wins: number
  losses: number
  draws: number
  fastestWin: number | null
  gameHistory: SavedGame[]
}

interface GameStore extends GameState, GameStats {
  setGameState: (state: Partial<GameState>) => void
  setDifficulty: (difficulty: GameState['aiDifficulty']) => void
  setBotBattle: (config: Partial<BotBattleConfig>) => void
  selectPiece: (pos: Position | null) => void
  resetGame: () => void
  setAnimating: (anim: GameState['animatingPiece']) => void
  recordGameResult: (result: SavedGame['result'], moves: Move[], opponent: string) => void
}

const initialGameState: GameState = {
  gameId: null,
  pieces: [],
  currentPlayer: 1,
  winner: null,
  legalMoves: [],
  moveHistory: [],
  selectedPiece: null,
  validMoves: [],
  aiDifficulty: 'advanced',
  playerColor: 1,
  marbleStyle: 'alley-glass',
  isAIThinking: false,
  animatingPiece: null,
  botBattle: {
    enabled: false,
    bot1Difficulty: 'medium',
    bot2Difficulty: 'medium',
    bot1First: true,
  },
}

const initialStats: GameStats = {
  gamesPlayed: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  fastestWin: null,
  gameHistory: [],
}

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      ...initialGameState,
      ...initialStats,

      setGameState: (state) => set(state),

      setDifficulty: (aiDifficulty) => set({ aiDifficulty }),

      setBotBattle: (botBattle) => set((state) => ({ botBattle: { ...state.botBattle, ...botBattle } })),

      selectPiece: (pos) => {
        const { pieces, legalMoves, playerColor, currentPlayer, winner } = get()

        if (winner !== null || currentPlayer !== playerColor) return

        if (pos === null) {
          set({ selectedPiece: null, validMoves: [] })
          return
        }

        const piece = pieces.find(
          (candidate) =>
            candidate.position.q === pos.q &&
            candidate.position.r === pos.r &&
            candidate.player === playerColor,
        )

        if (!piece) {
          set({ selectedPiece: null, validMoves: [] })
          return
        }

        set({
          selectedPiece: pos,
          validMoves: legalMoves.filter(
            (move) => move.start.q === pos.q && move.start.r === pos.r,
          ),
        })
      },

      resetGame: () => set(initialGameState),

      setAnimating: (animatingPiece) => set({ animatingPiece }),

      recordGameResult: (result, moves, opponent) => {
        const { gamesPlayed, wins, losses, draws, fastestWin, gameHistory } = get()

        const savedGame: SavedGame = {
          id: `game_${Date.now()}`,
          date: new Date().toISOString(),
          result,
          moves,
          opponent,
          moveCount: moves.length,
        }

        set({
          gamesPlayed: gamesPlayed + 1,
          wins: result === 'win' ? wins + 1 : wins,
          losses: result === 'loss' ? losses + 1 : losses,
          draws: result === 'draw' ? draws + 1 : draws,
          fastestWin:
            result === 'win'
              ? fastestWin === null
                ? moves.length
                : Math.min(fastestWin, moves.length)
              : fastestWin,
          gameHistory: [savedGame, ...gameHistory].slice(0, 50),
        })
      },
    }),
    {
      name: 'chinese-checkers-storage',
      partialize: (state) => ({
        aiDifficulty: state.aiDifficulty,
        marbleStyle: state.marbleStyle,
        gamesPlayed: state.gamesPlayed,
        wins: state.wins,
        losses: state.losses,
        draws: state.draws,
        fastestWin: state.fastestWin,
        gameHistory: state.gameHistory,
      }),
    },
  ),
)

export function posEqual(a: Position | null, b: Position | null): boolean {
  if (!a || !b) return a === b
  return a.q === b.q && a.r === b.r
}
